import React, { useCallback, useEffect, useState } from "react";
import { Download, Plus, Search, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RepairForm } from "./RepairForm";
import { RepairDetail } from "./RepairDetail";

export const REPAIR_TONE = {
  Draft: "neutral", Sent: "info", Approved: "success", Rejected: "danger",
  "Repair in Progress": "warning", Completed: "primary", Invoiced: "success", Cancelled: "danger",
};

export default function Repairs() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ statuses: [], engineers: [] });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [engineer, setEngineer] = useState("all");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/repairs", { params: { q, status, engineer } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, status, engineer]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/repairs/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const open = rows.filter((r) => ["Draft", "Sent", "Approved", "Repair in Progress"].includes(r.status));
  const awaiting = rows.filter((r) => r.status === "Sent");
  const inProgress = rows.filter((r) => r.status === "Repair in Progress");
  const value = open.reduce((a, r) => a + Number(r.total || 0), 0);

  const columns = [
    {
      key: "customer", label: "Customer / equipment",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.customer_name}</p>
          <p className="num truncate text-xs text-muted-foreground">
            {r.number}{r.rev ? ` Rev ${r.rev}` : ""} &middot; {r.equipment || "equipment"}
            {r.serial_no ? ` \u00b7 ${r.serial_no}` : ""}
          </p>
        </div>
      ),
    },
    { key: "complaint", label: "Complaint", render: (r) => <span className="line-clamp-2 text-sm text-muted-foreground">{r.complaint || "\u2014"}</span>, hideMobile: true },
    { key: "engineer", label: "Engineer", render: (r) => <span className="text-sm">{r.engineer_name || "\u2014"}</span>, hideMobile: true },
    { key: "date", label: "Raised", render: (r) => <span className="text-sm text-muted-foreground">{fdate(r.date)}</span>, hideMobile: true },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={REPAIR_TONE[r.status]} /> },
    { key: "total", label: "Estimate", align: "right", render: (r) => <span className="num text-sm font-semibold">{money(r.total)}</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Wrench}
        title="Repair Estimates"
        subtitle="Inspect, quote, get approval and turn a finished repair into an invoice without retyping anything."
        actions={
          <div className="flex flex-wrap gap-2">
            {rows.length > 0 && can("repairs", "export") && (
              <Button variant="outline" data-testid="repair-export-btn" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  Estimate: r.number, Revision: r.rev, Customer: r.customer_name, Company: r.company,
                  Equipment: r.equipment, Model: r.model_no, Serial: r.serial_no, Complaint: r.complaint,
                  Engineer: r.engineer_name, Status: r.status, Date: r.date, Total: r.total,
                  Invoice: r.invoice_number || "",
                })), "repair-estimates.csv");
                toast.success("Exported");
              }}>
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
            {can("repairs", "add") && (
              <Button data-testid="new-repair-btn" onClick={() => { setEditing(null); setFormOpen(true); }}>
                <Plus className="h-4 w-4" /> New estimate
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="repair-kpis">
        <StatCard label="Open jobs" value={open.length} icon="Wrench" tone="info" testId="kpi-repair-open" />
        <StatCard label="Awaiting approval" value={awaiting.length} icon="MailQuestion" tone="warning" testId="kpi-repair-awaiting" />
        <StatCard label="On the bench" value={inProgress.length} icon="Hammer" tone="primary" testId="kpi-repair-progress" />
        <StatCard label="Value in pipeline" value={value} currency icon="IndianRupee" tone="success" testId="kpi-repair-value" />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="repair-search"
            placeholder="Search by customer, estimate, model or serial number" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]" data-testid="repair-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open only</SelectItem>
            {(meta.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={engineer} onValueChange={setEngineer}>
          <SelectTrigger className="w-[180px]" data-testid="repair-engineer-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All engineers</SelectItem>
            {(meta.engineers || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable columns={columns} rows={rows} onRowClick={(r) => setDetailId(r.id)}
          rowTestId={(r) => `repair-row-${r.number}`}
          rowClassName={(r) => r.status === "Cancelled" && "opacity-60"}
          empty={<EmptyState icon={Wrench} title="No repair estimates yet"
            description="Log the complaint, add the parts and labour, and email a professional estimate to the customer."
            action={can("repairs", "add") && (
              <Button data-testid="repair-empty-new-btn" onClick={() => setFormOpen(true)}>
                <Plus className="h-4 w-4" /> New estimate
              </Button>
            )} />} />
      )}

      <RepairForm open={formOpen} job={editing} meta={meta}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        onSaved={(id) => { load(); if (id) setDetailId(id); }} />
      <RepairDetail jobId={detailId} open={!!detailId} meta={meta}
        onOpenChange={(v) => !v && setDetailId(null)}
        onEdit={(job) => { setEditing(job); setDetailId(null); setFormOpen(true); }}
        onChanged={load} />
    </div>
  );
}
